import {Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {MOMDTO, MOMService} from '../api/mom-api.service';
import {ActivatedRoute} from '@angular/router';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize, forkJoin, Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {map, switchMap} from 'rxjs/operators';
import {UserApiService} from '../api/user-api.service';
import {DatePipe} from '@angular/common';
import {DataTableDirective} from 'angular-datatables';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';
import {RoleScope, reachesAllChapters, reachesChapter, storedRoleScope} from '../shared/access';

declare const $: any; // Declare jQuery if you're using it globally

@Component({
    selector: 'app-chapter-mom',
    templateUrl: './chapter-mom.component.html',
    styleUrls: ['./chapter-mom.component.css'],
    providers: [DatePipe]
})
export class ChapterMomComponent implements OnDestroy {
    @ViewChild('editMomModal') editMomModalRef!: ElementRef;
    @ViewChild('addMomModal') addMomModalRef!: ElementRef;
    @ViewChild(DataTableDirective) dtElement!: DataTableDirective;

    userChapter: any;
    username: any;
    userRole: any;
    userGroup: any;
    roleScope: RoleScope = storedRoleScope();

    /** True for roles whose reach is every chapter (system administrator and the NEC tier), as decided by the API. */
    get allChapters(): boolean {
        return reachesAllChapters(this.roleScope);
    }
    selectedMom: MOMDTO | null = null;
    moms: MOMDTO[] = [];
    chapterId: string = '';
    userId: string = '';

    dtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    dtTrigger: Subject<any> = new Subject();

    newMom: MOMDTO = {
        title: '',
        chapterId: '',
        userId: '',
        createdDate: '',
        createdBy: '',
        updatedBy: '',
        updatedDate: '',
        description: '',
        visible: true
    };

    constructor(
        private route: ActivatedRoute,
        private momService: MOMService,
        private userApiService: UserApiService,
        private spinner: SpinnerService,
        private toastr: ToastrService,
        private datePipe: DatePipe
    ) {
    }

    ngOnInit(): void {
        this.dtOptions = ranaDtOptions({
            columns: [
                {title: 'Date and Time', data: 'createdDate', render: (d: any) => this.datePipe.transform(d, 'medium') || ''},
                {title: 'Created By', data: 'createdByName', render: (d: any) => escapeHtml(d)},
                {title: 'Title', data: 'title', render: (d: any) => escapeHtml(d)}
            ],
            order: [[0, 'desc']],
            rowCallback: (row: Node, data: any) => {
                const el = row as HTMLElement;
                $(el).off('click').on('click', () => {
                    this.selectedMom = data;
                    $('#modal-view-mom').modal('show');
                });
                el.style.cursor = 'pointer';
                return row;
            }
        });

        this.route.paramMap.subscribe(params => {
            const chapterId = params.get('id') || '';
            const storedUserId = sessionStorage.getItem('userId') || '';
            const storedUserName = sessionStorage.getItem('username') || '';

            this.chapterId = chapterId;
            this.newMom.chapterId = chapterId;
            this.newMom.userId = storedUserId;
            this.moms = [];
            this.viewMOMs(chapterId);

            this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
            this.userChapter = sessionStorage.getItem('chapter');
            this.username = storedUserName;
            this.userGroup = sessionStorage.getItem('userGroup') || '{}';
            this.roleScope = storedRoleScope();
        });

    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    private refreshTableData(): void {
        this.dtOptions.data = this.moms.filter(m => !(this.userGroup === 'NON_MEMBER' && !m.visible));
        this.rerenderTable();
    }

    private rerenderTable(): void {
        if (this.dtElement?.dtInstance) {
            this.dtElement.dtInstance.then(dt => {
                dt.destroy();
                this.dtTrigger.next(null);
            });
        } else {
            this.dtTrigger.next(null);
        }
    }

    viewMOMs(id: string): void {
        this.spinner.show();
        this.momService.getMOMsByChapterId(id).pipe(
            switchMap((data) => {

                // Sort by createdDate descending
                const sortedMoms = data.sort(
                    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
                );

                data.forEach(mom => {
                    if (mom.createdDate) {
                        // Simply parse without forcing UTC
                        const parsed = new Date(mom.createdDate);
                        if (!isNaN(parsed.getTime())) {
                            mom.createdDate = parsed;
                        }
                    }
                });


                // For each mom, call userApi to get user details
                const requests = sortedMoms.map(mom =>
                    this.userApiService.getShortData(mom.createdBy).pipe(
                        map(user => ({
                            ...mom,
                            createdByName: user.firstname + ' ' + user.lastname   // attach name
                        }))
                    )
                );

                // Run all requests in parallel
                return forkJoin(requests);

            }),
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (enrichedMoms) => {
                this.moms = enrichedMoms;
                this.refreshTableData();
            },
            error: (err) => {
                console.error('Error fetching MOMs:', err);
            },
        });
    }

    /* viewMOMs(id: string): void {
       this.spinner.show();
       this.momService.getMOMsByChapterId(id).pipe(
         finalize(() => this.spinner.hide())
       ).subscribe({
         next: (data) => {
           this.moms =  data.sort((a, b) =>
           new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
         );
         },
         error: (err) => {
           console.error('Error fetching MOMs:', err);
         },
       });
     }*/

    addMom(): void {
        this.spinner.show();
        this.momService.createMOM(this.newMom).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (data) => {
                this.viewMOMs(this.chapterId);
                this.toastr.success('MOM added successfully');
                this.newMom = {
                    title: '',
                    chapterId: this.chapterId,
                    userId: this.username,
                    createdDate: '',
                    createdBy: '',
                    updatedBy: '',
                    updatedDate: '',
                    description: '',
                    visible: true
                };
                this.hideAddMomModal();
            },
            error: (err) => {
                console.error('Error creating MOM:', err);
                this.toastr.error('Failed to create MOM');
            }
        });
    }

    hideAddMomModal(): void {
        if (this.addMomModalRef && this.addMomModalRef.nativeElement) {
            // Use jQuery to hide the modal for Bootstrap 4
            $(this.addMomModalRef.nativeElement).modal('hide');
        }
    }

    editingMom: MOMDTO | null = null;

    openEditModal(mom: MOMDTO): void {

        this.selectedMom = null;
        setTimeout(() => {
            this.selectedMom = {...mom};
        }, 0);

    }

    updateMOM(id: string | undefined, updatedMOM: MOMDTO): void {
        if (!id) {
            console.error('MOM ID is undefined. Cannot update.');
            this.toastr.error('Invalid MOM ID. Update failed.');
            return;
        }

        this.spinner.show();
        this.momService.updateMOM(id, updatedMOM).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (data) => {
                console.log('MOM updated:', data);
                this.toastr.success('MOM updated successfully');
                this.viewMOMs(this.chapterId);
                this.hideEditMomModal(); // Call the method to hide the edit modal
                this.hideViewMomModal();
            },
            error: (err) => {
                console.error('Error updating MOM:', err);
                this.toastr.error('Update failed. Please try again.');
            },
        });
    }

    hideEditMomModal(): void {
        if (this.editMomModalRef && this.editMomModalRef.nativeElement) {
            // Use jQuery to hide the modal for Bootstrap 4
            $(this.editMomModalRef.nativeElement).modal('hide');
        }
    }

    deleteMOM(id: string | undefined): void {
        if (!id) {
            console.error('MOM ID is undefined. Cannot delete.');
            this.toastr.error('Unable to delete MOM. Invalid ID.', 'Error');
            return;
        }

        this.spinner.show();
        this.momService.deleteMOM(id).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                // Remove deleted MOM locally
                this.moms = this.moms.filter(m => m.id !== id);
                this.refreshTableData();
                this.toastr.success('MOM deleted successfully');
                this.hideViewMomModal();
            },
            error: (err) => {
                console.error('Error deleting MOM:', err);
                this.toastr.error('Failed to delete MOM');
            }
        });
    }

    hideViewMomModal(): void {
        const viewMomModalElement = document.getElementById('modal-view-mom');
        if (viewMomModalElement) {
            $(viewMomModalElement).modal('hide');
        }
    }

    momInitials(name: string | undefined): string {
        const parts = (name || '').trim().split(/\s+/).filter(p => p);
        const initials = (parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '');
        return initials.toUpperCase() || '?';
    }

}